import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { AuthorizedContact } from "../../../../../resources-vue/vue-models/authorized-contact";
import { CAHPSSurveyMethod, ContactRoles, HttpStatusCodeEnum, Relationships } from "../../../../../enums/enums";
import { ToastrService } from "../../../../../services/toastr.service";
import { ReferralService } from "../../../../../services/referral-service";
import { PatientService } from "../../../../../services/patient.service";
import { useHospiceConvertToPending } from "../../store/useHospiceConvertToPending";
import { IContactsOrder, IContactsOrderQueryParam } from "../../../../../resources-vue/vue-interfaces/i-get-patient";
import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import AuthorizedContactForm from "./AuthorizedContactForm/AuthorizedContactForm.vue";
import AuthorizedContactList from "./AuthorizedContactList/AuthorizedContactList.vue";
import CahpsSurveyRecipient from "./CahpsSurveyRecipient/CahpsSurveyRecipient.vue";

export default defineComponent({
    components: {
        FormSection,
        AuthorizedContactForm,
        AuthorizedContactList,
        CahpsSurveyRecipient
    },
    data() {
        return {
            _toastrService: null,
            _referralService: null,
            _patientService: null,
            cahpsSurveyRecipientIndex: -1,
            totalContacts: 0,
            contacts: [],
            isGetContactsLoading: false,
            isGetContactsError: false,
            contactLimit: 10,
            showContactForm: false,
            isReOrderRequested: false,
            isLoading: false,
            isSavingReOrder: false,
            cachedContact: [],
            relationshipsEnum: Array.from(Relationships.enumWithDescriptions.values()),
            contactRolesEnum: Array.from(ContactRoles.enumWithDescriptions.values()),
            cahpsSurveyContactEnum: Array.from(CAHPSSurveyMethod.enumWithDescriptions.values()),
            contact: new AuthorizedContact()
        };
    },
    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._patientService = Container.instance.get(PatientService);
    },

    mounted() {
        this.getAuthorizedContacts();
    },

    computed: {
        ...mapState(useHospiceConvertToPending, ["accountId", "application", "patientDetails", "patientId"])
    },

    methods: {
        openAddContact() {
            this.showContactForm = true;
            this.contact.contactOrder = this.totalContacts;
            this.contact.application = Number(this.application);
        },
        closeAddContact() {
            this.isLoading = false;
            this.showContactForm = false;
            this.contact = new AuthorizedContact();
        },
        async addContact() {
            const validated = await (this.$refs.addContact as any).v$.$validate();
            if (this.isEmptyContact(this.contact)) {
                this._toastrService.error({
                    title: `Empty Contact`,
                    message: `Please add the necessary information to save the contact.`
                });
                return;
            }
            if (!validated) {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill required fields.`
                });
                return;
            }
            try {
                this.isLoading = true;
                this.contact = this.addressAndPhoneNumberCheck(this.contact);
                if (this.totalContacts === 0) {
                    this.contact.isPrimary = true;
                }
                this.contact.accountId = String(this.accountId);
                this.contact.application = Number(this.application);
                const response = await this._patientService.addAuthorizedContact(this.patientId, this.contact);
                if (response.isSuccess) {
                    this._toastrService.success({
                        title: "Success",
                        message: "Contact has been successfully added!"
                    });
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: "There was a problem while adding a contact. Please try again."
                    });
                    this.isLoading = false;
                }
                this.closeAddContact();
                await this.getAuthorizedContacts();
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: `Error`,
                    message: "There was a problem while adding a contact. Please try again."
                });
            } finally {
                this.isLoading = false;
            }
        },
        requestReOrder() {
            this.cachedContact = this.contacts.length > 0 ? [...this.contacts] : [];
            this.isReOrderRequested = true;
        },
        async confirmReOrder() {
            try {
                this.isSavingReOrder = true;
                let contactsOrder: IContactsOrder[] = this.contacts.map((contact: AuthorizedContact) => {
                    return {
                        contactId: contact.id,
                        contactOrder: contact.contactOrder
                    };
                });
                await this.reorderContacts(contactsOrder);
                await this.getAuthorizedContacts();
            } catch (e) {
                if (e.status === HttpStatusCodeEnum.NotModified) {
                    this._toastrService.info({
                        title: "No Changes Detected",
                        message: "There were no changes made to the contacts order."
                    });
                } else {
                    console.error(e);
                    this._toastrService.error({
                        title: `Error`,
                        message: "There was a problem while re-ordering the contacts. Please try again"
                    });
                }
            } finally {
                this.isReOrderRequested = false;
                this.isSavingReOrder = false;
            }
        },
        deleteAuthorizedContact(contactId: string) {
            const payload = {
                contactId: contactId,
                application: Number(this.application),
                accountId: String(this.accountId)
            };
            return this._patientService.deleteAuthorizedContact(payload);
        },
        updateAuthorizedContact(contact: AuthorizedContact) {
            contact.referralId = this.patientId as string;
            // will remove the comment after backend fixed the code
            // contact.accountId = String(this.accountId);
            contact.application = Number(this.application);
            return this._patientService.updateAuthorizedContact(this.accountId, contact);
        },
        async getAuthorizedContacts() {
            try {
                this.isGetContactsLoading = true;
                const response = await this.getContacts();
                this.contacts = response.data;
                this.totalContacts = this.contacts.length;
            } catch (e) {
                console.error(e);
                this.isGetContactsError = true;
            } finally {
                this.isGetContactsLoading = false;
            }
        },
        updateIndices() {
            this.contacts = this.contacts?.map((contact: AuthorizedContact, index: number) => {
                contact.isPrimary = index === 0;
                contact.contactOrder = index;
                return contact;
            });
        },
        cancelReOrder() {
            if (this.$refs.contactList) {
                this.contacts = this.cachedContact;
            }
            this.isReOrderRequested = false;
        },
        isEmptyContact(contact: AuthorizedContact) {
            let isContactEmpty = false;
            isContactEmpty = Object.entries(contact).every((contactProperty) => {
                let isEmpty = true;
                let checkNotRequired = ["id", "isPrimary", "contactOrder", "address"];
                if (!checkNotRequired.includes(contactProperty[0])) {
                    if (Array.isArray(contactProperty[1])) {
                        isEmpty = contactProperty[1].length === 0;
                    } else {
                        isEmpty = !contactProperty[1];
                    }
                } else if (contactProperty[0] === "address" && !contactProperty[1].addressLine1) {
                    isEmpty = true;
                }
                return isEmpty;
            });
            return isContactEmpty;
        },
        addressAndPhoneNumberCheck(contact: AuthorizedContact) {
            if (!contact?.address?.addressLine1) {
                contact.address = null;
            }
            if (!contact?.phoneNumbers[0]?.number && !contact?.phoneNumbers[1]?.number) {
                contact.phoneNumbers = [];
            }
            return contact;
        },
        reorderContacts(contactOrder: IContactsOrder[]) {
            const contactsOrderQuery: IContactsOrderQueryParam = {
                contactsOrder: contactOrder,
                application: Number(this.application),
                accountId: String(this.accountId)
            };
            return this._patientService.reorderAuthorizedContacts(contactsOrderQuery);
        },
        getContacts() {
            const payload = {
                accountId: String(this.accountId),
                patientId: String(this.patientId),
                application: Number(this.application)
            };
            return this._patientService.getAuthorizedContacts(payload);
        }
    }
});
